import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSocket } from '../utils/socket';
import { useNotification } from './NotificationContext';

interface RealtimeContextType {
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  lastEvent: string | null;
  eventCount: number;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within a RealtimeProvider');
  }
  return context;
};

interface RealtimeProviderProps {
  children: React.ReactNode;
}

export const RealtimeProvider: React.FC<RealtimeProviderProps> = ({ children }) => {
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('connecting');
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [eventCount, setEventCount] = useState(0);
  const { showInfo, showError } = useNotification();
  const { socket, isConnected } = useSocket();

  // Update connection status based on socket state
  useEffect(() => {
    if (socket?.connected) {
      setConnectionStatus('connected');
    } else if (socket) {
      setConnectionStatus('connecting');
    } else {
      setConnectionStatus('disconnected');
    }
  }, [socket, socket?.connected]);

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      setConnectionStatus('connected');
      // Don't show notification - status indicator is enough
    };

    const handleDisconnect = () => {
      setConnectionStatus('disconnected');
      // Don't show notification - status indicator is enough
    };

    const handleError = (error: any) => {
      setConnectionStatus('error');
      showError('Real-time connection error');
      console.error('Socket error:', error);
    };

    // Dashboard events
    const handleDashboardUpdate = (data: any) => {
      setLastEvent('dashboard_update');
      setEventCount(prev => prev + 1);
      showInfo('Dashboard data updated');
    };

    // User events
    const handleUserCreated = (data: any) => {
      setLastEvent('user_created');
      setEventCount(prev => prev + 1);
      showInfo(`New user registered: ${data.user?.name || 'Unknown'}`);
    };

    const handleUserUpdated = (data: any) => {
      setLastEvent('user_updated');
      setEventCount(prev => prev + 1);
    };

    const handleUserDeleted = (data: any) => {
      setLastEvent('user_deleted');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['users']);
      // queryClient.invalidateQueries(['dashboard']);
    };

    // Booking events
    const handleBookingCreated = (data: any) => {
      setLastEvent('booking_created');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['bookings']);
      // queryClient.invalidateQueries(['dashboard']);
      showInfo(`New booking created: #${data.booking?.id?.slice(-6) || 'Unknown'}`);
    };

    const handleBookingUpdated = (data: any) => {
      setLastEvent('booking_updated');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['bookings']);
      // queryClient.invalidateQueries(['booking', data.bookingId]);
      // queryClient.invalidateQueries(['dashboard']);
      
      if (data.status) {
        showInfo(`Booking #${data.bookingId?.slice(-6) || 'Unknown'} status updated to ${data.status}`);
      }
    };

    const handleBookingCancelled = (data: any) => {
      setLastEvent('booking_cancelled');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['bookings']);
      // queryClient.invalidateQueries(['dashboard']);
      showInfo(`Booking #${data.bookingId?.slice(-6) || 'Unknown'} was cancelled`);
    };

    // Service events
    const handleServiceCreated = (data: any) => {
      setLastEvent('service_created');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['services']);
      showInfo(`New service created: ${data.service?.name || 'Unknown'}`);
    };

    const handleServiceUpdated = (data: any) => {
      setLastEvent('service_updated');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['services']);
      // queryClient.invalidateQueries(['service', data.serviceId]);
    };

    const handleServiceDeleted = (data: any) => {
      setLastEvent('service_deleted');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['services']);
    };

    // Worker events
    const handleWorkerRegistered = (data: any) => {
      setLastEvent('worker_registered');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['workers']);
      // queryClient.invalidateQueries(['dashboard']);
      showInfo(`New worker registered: ${data.worker?.name || 'Unknown'}`);
    };

    const handleWorkerApproved = (data: any) => {
      setLastEvent('worker_approved');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['workers']);
      // queryClient.invalidateQueries(['worker', data.workerId]);
      showInfo(`Worker ${data.workerId?.slice(-6) || 'Unknown'} was approved`);
    };

    const handleWorkerRejected = (data: any) => {
      setLastEvent('worker_rejected');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['workers']);
      // queryClient.invalidateQueries(['worker', data.workerId]);
      showInfo(`Worker ${data.workerId?.slice(-6) || 'Unknown'} was rejected`);
    };

    // Moderation events
    const handleModerationPending = (data: any) => {
      setLastEvent('moderation_pending');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['moderation']);
      showInfo(`New item pending moderation: ${data.type || 'Unknown'}`);
    };

    const handleModerationApproved = (data: any) => {
      setLastEvent('moderation_approved');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['moderation']);
      showInfo(`Item approved: ${data.type || 'Unknown'}`);
    };

    const handleModerationRejected = (data: any) => {
      setLastEvent('moderation_rejected');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['moderation']);
      showInfo(`Item rejected: ${data.type || 'Unknown'}`);
    };

    // Notification events
    const handleNotificationSent = (data: any) => {
      setLastEvent('notification_sent');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['notifications']);
      showInfo(`Notification sent: ${data.title || 'Unknown'}`);
    };

    // Offer events
    const handleOfferCreated = (data: any) => {
      setLastEvent('offer_created');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['offers']);
      showInfo(`New offer created: ${data.offer?.title || 'Unknown'}`);
    };

    const handleOfferUsed = (data: any) => {
      setLastEvent('offer_used');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['offers']);
      // queryClient.invalidateQueries(['offer', data.offerId]);
    };

    // Blog events
    const handleBlogPostCreated = (data: any) => {
      setLastEvent('blog_post_created');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['blog']);
      showInfo(`New blog post created: ${data.post?.title || 'Unknown'}`);
    };

    const handleBlogPostPublished = (data: any) => {
      setLastEvent('blog_post_published');
      setEventCount(prev => prev + 1);
      // queryClient.invalidateQueries(['blog']);
      showInfo(`Blog post published: ${data.post?.title || 'Unknown'}`);
    };

    // System events
    const handleSystemAlert = (data: any) => {
      setLastEvent('system_alert');
      setEventCount(prev => prev + 1);
      showError(`System Alert: ${data.message || 'Unknown issue'}`);
    };

    const handleSystemMaintenance = (data: any) => {
      setLastEvent('system_maintenance');
      setEventCount(prev => prev + 1);
      showInfo(`System Maintenance: ${data.message || 'Scheduled maintenance'}`);
    };

    // Register event listeners
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('error', handleError);

    // Dashboard events
    socket.on('dashboard:update', handleDashboardUpdate);

    // User events
    socket.on('user:created', handleUserCreated);
    socket.on('user:updated', handleUserUpdated);
    socket.on('user:deleted', handleUserDeleted);

    // Booking events
    socket.on('booking:created', handleBookingCreated);
    socket.on('booking:updated', handleBookingUpdated);
    socket.on('booking:cancelled', handleBookingCancelled);

    // Service events
    socket.on('service:created', handleServiceCreated);
    socket.on('service:updated', handleServiceUpdated);
    socket.on('service:deleted', handleServiceDeleted);

    // Worker events
    socket.on('worker:registered', handleWorkerRegistered);
    socket.on('worker:approved', handleWorkerApproved);
    socket.on('worker:rejected', handleWorkerRejected);

    // Moderation events
    socket.on('moderation:pending', handleModerationPending);
    socket.on('moderation:approved', handleModerationApproved);
    socket.on('moderation:rejected', handleModerationRejected);

    // Notification events
    socket.on('notification:sent', handleNotificationSent);

    // Offer events
    socket.on('offer:created', handleOfferCreated);
    socket.on('offer:used', handleOfferUsed);

    // Blog events
    socket.on('blog:post_created', handleBlogPostCreated);
    socket.on('blog:post_published', handleBlogPostPublished);

    // System events
    socket.on('system:alert', handleSystemAlert);
    socket.on('system:maintenance', handleSystemMaintenance);

    return () => {
      // Cleanup event listeners
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('error', handleError);

      socket.off('dashboard:update', handleDashboardUpdate);
      socket.off('user:created', handleUserCreated);
      socket.off('user:updated', handleUserUpdated);
      socket.off('user:deleted', handleUserDeleted);
      socket.off('booking:created', handleBookingCreated);
      socket.off('booking:updated', handleBookingUpdated);
      socket.off('booking:cancelled', handleBookingCancelled);
      socket.off('service:created', handleServiceCreated);
      socket.off('service:updated', handleServiceUpdated);
      socket.off('service:deleted', handleServiceDeleted);
      socket.off('worker:registered', handleWorkerRegistered);
      socket.off('worker:approved', handleWorkerApproved);
      socket.off('worker:rejected', handleWorkerRejected);
      socket.off('moderation:pending', handleModerationPending);
      socket.off('moderation:approved', handleModerationApproved);
      socket.off('moderation:rejected', handleModerationRejected);
      socket.off('notification:sent', handleNotificationSent);
      socket.off('offer:created', handleOfferCreated);
      socket.off('offer:used', handleOfferUsed);
      socket.off('blog:post_created', handleBlogPostCreated);
      socket.off('blog:post_published', handleBlogPostPublished);
      socket.off('system:alert', handleSystemAlert);
      socket.off('system:maintenance', handleSystemMaintenance);
    };
  }, [socket, showInfo, showError]);

  const contextValue: RealtimeContextType = {
    isConnected,
    connectionStatus,
    lastEvent,
    eventCount,
  };

  return (
    <RealtimeContext.Provider value={contextValue}>
      {children}
    </RealtimeContext.Provider>
  );
};

export default RealtimeProvider;
